<template>
  <div class="interest-calculation-detail">
    <div class="page-header hide-on-print">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>延期费用记录查看</span>
      </div>
      <div class="header-actions">
        <template v-if="record.status === 'DRAFT'">
          <!-- 供应商审核：仅选中的供应商审核人可见 -->
          <el-button v-if="isSupplierAuditor && record.needSupplierAudit" type="primary" size="small" @click="supplierAudit" :loading="auditLoading">供应商审核通过</el-button>
        </template>
        <template v-if="record.status === 'ACTIVE'">
          <!-- 需求方确认：仅合同中需求方单位联系人可见，且不是审核人时显示（审核人看到的是审核按钮） -->
          <el-button v-if="isDemandUnit && !isDemandAuditor" type="success" size="small" @click="confirmDialogVisible = true">需求方确认</el-button>
          <!-- 需求方审核：仅选中的需求方审核人可见 -->
          <el-button v-if="isDemandAuditor" type="primary" size="small" @click="demandAudit" :loading="auditLoading">需求方审核通过</el-button>
        </template>
        <template v-if="record.status === 'EXECUTING'">
          <!-- 回退为待确认：仅创建人可见（与后端校验一致） -->
          <el-button v-if="isCreator" type="warning" size="small" @click="rollbackRecord" :loading="rollbackLoading">回退为待确认</el-button>
        </template>
        <el-button v-permission="'finance:interest-management:print'" type="success" size="small" icon="el-icon-printer" @click="handlePrint">打印</el-button>
        <el-button size="small" icon="el-icon-back" @click="goBack">返回列表</el-button>
      </div>
    </div>

    <!-- 加载中 -->
    <div v-if="loading" class="loading-wrap">
      <el-skeleton :rows="8" animated />
    </div>

    <div v-else class="content">
      <!-- 延期费用记录基本信息 -->
      <el-card class="info-card hide-on-print">
        <div slot="header" class="card-header">延期费用记录信息</div>
        <el-descriptions border :column="3">
          <el-descriptions-item label="截止时间">{{ formatCutoffDate(record.cutoffDate) }}</el-descriptions-item>
          <el-descriptions-item label="合同名称">{{ record.contractName }}</el-descriptions-item>
          <el-descriptions-item label="需求方名称">{{ record.demandUnitNames || record.demandUnitName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="项目名称">{{ record.projectName || '-' }}</el-descriptions-item>
          <el-descriptions-item label="结算总额">
            <span class="amount-text">{{ formatAmount(record.settlementTotalAmount) }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="延期费用总额">
            <span class="interest-text">{{ formatAmount(record.interestTotalAmount) }}</span>
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="statusTagType">{{ statusText }}</el-tag>
          </el-descriptions-item>
          <el-descriptions-item v-if="record.needDemandAudit && record.demandAuditorName" label="需求方审核人">{{ record.demandAuditorName }}</el-descriptions-item>
          <el-descriptions-item v-if="record.needSupplierAudit && record.supplierAuditorName" label="供应商审核人">{{ record.supplierAuditorName }}</el-descriptions-item>
        </el-descriptions>
      </el-card>

      <!-- 结算单明细 -->
      <el-card class="table-card hide-on-print">
        <div slot="header" class="card-header">结算单明细</div>
        <el-table
          :data="settlementList"
          border
          size="small"
          show-summary
          :summary-method="getSummaryMethod"
        >
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column label="结算单号" align="center" prop="settlementNumber" min-width="140" />
          <el-table-column label="结算单标题" min-width="160" align="center" show-overflow-tooltip>
            <template slot-scope="scope">{{ scope.row.settlementTitle || '-' }}</template>
          </el-table-column>
          <el-table-column label="结算时间" align="center">
            <template slot-scope="scope">{{ formatSettlementDate(scope.row.settlementDate) }}</template>
          </el-table-column>
          <el-table-column label="结算金额" align="center" width="120">
            <template slot-scope="scope">
              <span class="amount-text">{{ formatAmount(scope.row.settlementAmount) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="已付金额" align="center" width="120">
            <template slot-scope="scope">
              <span class="amount-text">{{ formatAmount(scope.row.paidAmount) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="未付金额" align="center" width="120">
            <template slot-scope="scope">
              <span class="amount-text">{{ formatAmount(scope.row.remainingAmount) }}</span>
            </template>
          </el-table-column>
          <el-table-column label="付款记录" align="center" width="100">
            <template slot-scope="scope">
              <el-button
                type="text"
                size="small"
                :disabled="!(scope.row.paymentFlowCount > 0)"
                @click="showPaymentDetail(scope.row)"
              >
                {{ scope.row.paymentFlowCount > 0 ? `共${scope.row.paymentFlowCount}条` : '-' }}
              </el-button>
            </template>
          </el-table-column>
          <el-table-column label="延期费用金额" align="center" width="120">
            <template slot-scope="scope">
              <span class="interest-text">{{ formatAmount(scope.row.interestAmount) }}</span>
            </template>
          </el-table-column>
        </el-table>
      </el-card>
    </div>

    <!-- 付款明细弹窗 -->
    <el-dialog
      title="付款明细"
      :visible.sync="paymentDetailVisible"
      width="900px"
      append-to-body
    >
      <div v-if="currentSettlement" class="payment-detail-dialog">
        <p class="dialog-settlement-info">
          <span>结算单号：{{ currentSettlement.settlementNumber }}</span>
          <span v-if="currentSettlement.settlementTitle" style="margin-left: 16px">结算单标题：{{ currentSettlement.settlementTitle }}</span>
        </p>
        <el-table :data="paymentFlows" border size="small" v-loading="flowsLoading">
          <el-table-column type="index" label="序号" width="60" align="center" />
          <el-table-column label="收付款时间" align="center" min-width="150">
            <template slot-scope="scope">{{ formatDateTime(scope.row.transactionTime) }}</template>
          </el-table-column>
          <el-table-column label="资金类型" width="90" align="center">
            <template slot-scope="scope">{{ scope.row.fundType === 'PAYABLE' ? '付款' : '收款' }}</template>
          </el-table-column>
          <el-table-column label="已付金额" align="center" width="120">
            <template slot-scope="scope">{{ formatAmount(scope.row.paidAmount) }}</template>
          </el-table-column>
          <el-table-column label="利率类型" width="90" align="center">
            <template slot-scope="scope">{{ formatInterestRateType(scope.row.interestRateType) }}</template>
          </el-table-column>
          <el-table-column label="逾期天数" align="center" width="90">
            <template slot-scope="scope">{{ scope.row.overdueDays != null ? scope.row.overdueDays : '-' }}</template>
          </el-table-column>
          <el-table-column label="实际计算利率" align="center" width="110">
            <template slot-scope="scope">{{ formatAppliedInterestRate(scope.row.appliedInterestRate) }}</template>
          </el-table-column>
          <el-table-column label="延期费用金额" align="center" width="120">
            <template slot-scope="scope">{{ formatAmount(scope.row.interestAmount) }}</template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button size="small" @click="paymentDetailVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 需求方确认弹窗 -->
    <el-dialog title="需求方确认" :visible.sync="confirmDialogVisible" width="500px" :close-on-click-modal="false" @open="onConfirmDialogOpen">
      <el-form :model="confirmForm" :rules="confirmFormRules" ref="confirmForm" label-width="140px">
        <el-form-item label="是否需要审核" prop="needDemandAudit">
          <el-radio-group v-model="confirmForm.needDemandAudit">
            <el-radio :label="false">不需要，直接确认</el-radio>
            <el-radio :label="true">需要，指定审核人</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item v-if="confirmForm.needDemandAudit" label="选择审核人" prop="demandAuditorId">
          <el-select
            v-model="confirmForm.demandAuditorId"
            placeholder="请选择审核人"
            style="width: 100%"
            filterable
            :loading="auditorListLoading"
            loading-text="正在查询审核人..."
          >
            <el-option
              v-for="user in auditorList"
              :key="user.id"
              :label="`${user.realName || user.username} (${user.unitName || ''})`"
              :value="user.id"
            />
          </el-select>
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="confirmDialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitConfirm" :loading="confirmLoading">确认</el-button>
      </span>
    </el-dialog>

    <!-- 打印专用区域 -->
    <div id="interestPrintArea" class="print-only" ref="printArea">
      <div class="print-title">延期费用明细</div>
      <div class="print-info">
        <div class="print-row">
          <span class="print-label">截止时间</span>
          <span class="print-value">{{ formatCutoffDate(record.cutoffDate) }}</span>
        </div>
        <div class="print-row">
          <span class="print-label">合同名称</span>
          <span class="print-value">{{ record.contractName }}</span>
        </div>
        <div class="print-row">
          <span class="print-label">需求方名称</span>
          <span class="print-value">{{ record.demandUnitNames || record.demandUnitName }} {{ record.projectName }}</span>
        </div>
      </div>
      <table class="print-table">
        <thead>
          <tr>
            <th>结算单号</th>
            <th>结算单标题</th>
            <th>结算时间</th>
            <th>结算金额</th>
            <th>已付金额</th>
            <th>未付金额</th>
            <th>延期费用金额</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, idx) in settlementList" :key="idx">
            <td>{{ row.settlementNumber }}</td>
            <td>{{ row.settlementTitle || '-' }}</td>
            <td>{{ formatSettlementDate(row.settlementDate) }}</td>
            <td>{{ formatAmount(row.settlementAmount) }}</td>
            <td>{{ formatAmount(row.paidAmount) }}</td>
            <td>{{ formatAmount(row.remainingAmount) }}</td>
            <td>{{ formatAmount(row.interestAmount) }}</td>
          </tr>
          <!-- 合计放在 tbody 末尾，避免 tfoot 在分页打印时每页重复 -->
          <tr class="print-summary-row">
            <td colspan="3">合计</td>
            <td>{{ formatAmount(record.settlementTotalAmount) }}</td>
            <td>{{ formatAmount(settlementPaidTotal) }}</td>
            <td>{{ formatAmount(settlementRemainingTotal) }}</td>
            <td>{{ formatAmount(record.interestTotalAmount) }}</td>
          </tr>
          <tr class="print-summary-row">
            <td colspan="6">已结算延期费用</td>
            <td>{{ formatAmount(record.settledInterest) }}</td>
          </tr>
          <tr class="print-summary-row">
            <td colspan="6">本期新增延期费用</td>
            <td>{{ formatAmount(record.newInterest) }}</td>
          </tr>
        </tbody>
      </table>
      <table class="print-signature-table">
        <tr>
          <td class="signature-cell">
            <span class="signature-label">需求单位经办人：</span>
            <span class="signature-value">{{ record.confirmUserName || record.confirm_user_name || '' }}</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">操作时间：</span>
            <span class="signature-value">{{ formatPrintDate(record.confirmationTime || record.confirmation_time) }}</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">供应单位经办人：</span>
            <span class="signature-value">{{ record.creatorName || record.creator_name || '' }}</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">操作时间：</span>
            <span class="signature-value">{{ formatPrintDate(record.createTime || record.create_time) }}</span>
          </td>
        </tr>
        <tr>
          <td class="signature-cell">
            <span class="signature-label">需求单位审核人：</span>
            <span class="signature-value">{{ record.demandAuditorName || record.demand_auditor_name || '' }}</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">操作时间：</span>
            <span class="signature-value">{{ formatPrintDate(record.demandAuditTime || record.demand_audit_time) }}</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">供应单位审核人：</span>
            <span class="signature-value">{{ record.supplierAuditorName || record.supplier_auditor_name || '' }}</span>
          </td>
          <td class="signature-cell">
            <span class="signature-label">操作时间：</span>
            <span class="signature-value">{{ formatPrintDate(record.supplierAuditTime || record.supplier_audit_time) }}</span>
          </td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import {
  getInterestCalculationById,
  getInterestCalculationSettlements,
  getInterestCalculationFlows,
  demandConfirmInterestCalculation,
  supplierAuditInterestCalculation,
  demandAuditInterestCalculation,
  rollbackInterestCalculation
} from '@/api/modules/interest-calculation'
import { getInterestCalculationPrintData } from '@/api/modules/print'
import { getContractById } from '@/api/modules/contracts'
import { getUsersByUnitId, queryUsersPagination } from '@/api/modules/users'
import { getUserInfo } from '@/utils/auth'
import { getApiUrl } from '@/utils/env'
import { installPlatformPrintFooter } from '@/utils/print-footer'

export default {
  name: 'InterestCalculationDetail',
  data() {
    return {
      recordId: null,
      loading: true,
      confirmLoading: false,
      auditLoading: false,
      rollbackLoading: false,
      auditorLoading: false,
      flowsLoading: false,
      paymentDetailVisible: false,
      currentSettlement: null,
      paymentFlows: [],
      confirmDialogVisible: false,
      auditorList: [],
      auditorListLoading: false,
      currentUser: null,
      currentUnitId: null,
      demandUnitIds: [], // 合同中需求方单位ID，用于判断是否有确认权限
      confirmForm: {
        needDemandAudit: false,
        demandAuditorId: null
      },
      confirmFormRules: {
        needDemandAudit: [
          { required: true, message: '请选择是否需要审核', trigger: 'change' }
        ],
        demandAuditorId: [
          { validator: (rule, value, cb) => {
            if (this.confirmForm.needDemandAudit && !value) cb(new Error('请选择审核人'))
            else cb()
          }, trigger: 'change' }
        ]
      },
      record: {
        cutoffDate: '',
        contractName: '',
        demandUnitNames: '',
        demandUnitName: '',
        projectName: '',
        settlementTotalAmount: 0,
        interestTotalAmount: 0,
        settledInterest: 0,
        newInterest: 0,
        status: 'DRAFT',
        needDemandAudit: false,
        demandAuditorId: null,
        demandAuditorName: ''
      },
      settlementList: []
    }
  },
  computed: {
    statusText() {
      const map = { DRAFT: '待审核', ACTIVE: '待确认', EXECUTING: '已确认' }
      return map[this.record.status] || this.record.status || '-'
    },
    statusTagType() {
      const map = { DRAFT: 'info', ACTIVE: 'warning', EXECUTING: 'success' }
      return map[this.record.status] || 'info'
    },
    // 当前用户是否是合同中需求方单位（需求方单位联系人才能确认，参考结算逻辑）
    isDemandUnit() {
      if (!this.record || !this.currentUnitId) return false
      const ids = this.demandUnitIds && this.demandUnitIds.length
        ? this.demandUnitIds
        : (this.record.demandUnitIds || [])
      if (Array.isArray(ids) && ids.length) {
        return ids.some(id => id === this.currentUnitId || id == this.currentUnitId)
      }
      const demandId = this.record.demandUnitId || this.record.demand_unit_id
      return demandId != null && (demandId === this.currentUnitId || demandId == this.currentUnitId)
    },
    // 当前用户是否是供应商审核人（只有选中审核人可见审核按钮）
    isSupplierAuditor() {
      if (!this.record || !this.currentUser) return false
      const auditorId = this.record.supplierAuditorId || this.record.supplier_auditor_id
      const userId = this.currentUser.id || this.currentUser.userId
      return auditorId != null && (auditorId === userId || auditorId == userId)
    },
    // 当前用户是否是需求方审核人（只有选中审核人可见审核按钮）
    isDemandAuditor() {
      if (!this.record || !this.currentUser) return false
      const auditorId = this.record.demandAuditorId || this.record.demand_auditor_id
      const userId = this.currentUser.id || this.currentUser.userId
      return auditorId != null && (auditorId === userId || auditorId == userId)
    },
    // 当前用户是否是创建人（用于回退按钮）
    isCreator() {
      if (!this.record || !this.currentUser) return false
      const creatorId = this.record.creatorId || this.record.creator_id || this.record.createId || this.record.create_id
      const userId = this.currentUser.id || this.currentUser.userId
      return creatorId != null && (creatorId === userId || creatorId == userId)
    },
    /** 结算单明细：已付金额合计（与列表行求和一致） */
    settlementPaidTotal() {
      const list = this.settlementList || []
      return list.reduce((sum, row) => sum + Number(row.paidAmount ?? 0), 0)
    },
    /** 结算单明细：未付金额合计 */
    settlementRemainingTotal() {
      const list = this.settlementList || []
      return list.reduce((sum, row) => sum + Number(row.remainingAmount ?? 0), 0)
    }
  },
  watch: {
    'confirmForm.needDemandAudit'(val) {
      if (val) {
        this.getAuditorList()
      } else {
        this.confirmForm.demandAuditorId = null
        this.auditorList = []
      }
    }
  },
  created() {
    this.recordId = this.$route.params.id
    this.getCurrentUserInfo()
    this.getCurrentUnitId()
    this.loadRecord()
  },
  methods: {
    async loadRecord() {
      if (!this.recordId) {
        this.$message.warning('缺少记录ID')
        this.loading = false
        return
      }
      this.loading = true
      try {
        const [detailRes, settleRes] = await Promise.all([
          getInterestCalculationById(this.recordId),
          getInterestCalculationSettlements(this.recordId, { page: 0, size: 500 })
        ])
        const d = detailRes.data
        const s = settleRes.data
        if (d && d.success && d.data) {
          this.record = { ...this.record, ...d.data }
          // 获取合同中需求方单位ID，用于判断确认权限（后端未返回时从合同获取）
          const ids = this.record.demandUnitIds
          if (Array.isArray(ids) && ids.length) {
            this.demandUnitIds = ids
          } else if (this.record.contractId) {
            try {
              const contractRes = await getContractById(this.record.contractId)
              const c = contractRes.data
              if (c && c.success && c.data) {
                const contractIds = c.data.demandUnitIds || c.data.demand_unit_ids || []
                this.demandUnitIds = Array.isArray(contractIds) ? contractIds : []
              }
            } catch (e) {
              console.warn('获取合同需求方单位失败', e)
            }
          }
        } else {
          this.$message.error((d && d.message) || '加载详情失败')
          return
        }
        if (s && s.success && s.data) {
          const content = s.data.content || s.data || []
          this.settlementList = Array.isArray(content) ? content : []
        }
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message) || error.message || '加载详情失败'
        this.$message.error(msg)
      } finally {
        this.loading = false
      }
    },
    getCurrentUserInfo() {
      try {
        const userInfo = getUserInfo()
        if (userInfo) this.currentUser = userInfo
      } catch (err) {
        console.error('获取用户信息失败', err)
      }
    },
    getCurrentUnitId() {
      try {
        let userInfo = this.currentUser || getUserInfo()
        if (!userInfo || !userInfo.unitId) {
          const store = this.$store?.state || {}
          userInfo = store.user?.userInfo || store.userInfo || store.auth?.userInfo
        }
        if (!userInfo || !(userInfo.unitId ?? userInfo.unit_id)) {
          const local = localStorage.getItem('userInfo')
          if (local) {
            try {
              userInfo = JSON.parse(local)
            } catch (e) { /* ignore */ }
          }
        }
        if (userInfo) {
          this.currentUnitId = userInfo.unitId ?? userInfo.unit_id
        }
      } catch (e) {
        console.warn('获取单位ID失败', e)
      }
    },
    async getAuditorList() {
      if (!this.currentUser) {
        this.$message.warning('当前用户信息不存在，无法获取审核人列表')
        return
      }
      const roleId = this.currentUser.role?.id || this.currentUser.roleId || this.currentUser.role_id
      const unitId = this.currentUser.unitId || this.currentUser.unit_id
      const projectIds = this.currentUser.projectIds || this.currentUser.project_ids
      const currentUserId = this.currentUser.id || this.currentUser.userId
      this.auditorListLoading = true
      try {
        if (roleId === 2 && unitId) {
          const res = await getUsersByUnitId(unitId)
          if (res.data && res.data.success) {
            const allUsers = res.data.data || []
            this.auditorList = allUsers.filter(u => (u.id || u.userId) !== currentUserId)
            if (this.auditorList.length === 0) {
              this.$message.warning('本单位没有其他用户可以作为审核人')
            }
          }
        } else if (roleId === 3 && projectIds && projectIds.length) {
          const res = await queryUsersPagination({ projectIds: projectIds.join(','), page: 0, size: 100 })
          if (res.data && res.data.success) {
            const allUsers = res.data.data?.content || []
            this.auditorList = allUsers.filter(u => (u.id || u.userId) !== currentUserId)
            if (this.auditorList.length === 0) {
              this.$message.warning('同项目没有其他用户可以作为审核人')
            }
          }
        } else {
          this.$message.warning('无法获取审核人列表，请检查用户角色配置')
        }
      } catch (e) {
        this.$message.error((e.response && e.response.data && e.response.data.message) || e.message || '获取审核人失败')
      } finally {
        this.auditorListLoading = false
      }
    },
    async showPaymentDetail(row) {
      this.currentSettlement = row
      this.paymentFlows = []
      this.paymentDetailVisible = true
      if (!row.settlementId || !this.recordId) return
      this.flowsLoading = true
      try {
        const res = await getInterestCalculationFlows(this.recordId, row.settlementId)
        const data = res.data
        if (data && data.success && data.data) {
          this.paymentFlows = Array.isArray(data.data) ? data.data : []
        }
      } catch (e) {
        this.$message.error((e.response && e.response.data && e.response.data.message) || e.message || '加载流水失败')
      } finally {
        this.flowsLoading = false
      }
    },
    onConfirmDialogOpen() {
      this.confirmForm.needDemandAudit = false
      this.confirmForm.demandAuditorId = null
      this.$nextTick(() => this.$refs.confirmForm && this.$refs.confirmForm.clearValidate())
    },
    submitConfirm() {
      this.$refs.confirmForm.validate(async valid => {
        if (!valid) return
        this.confirmLoading = true
        try {
          const body = {}
          if (this.confirmForm.needDemandAudit) {
            body.needDemandAudit = true
            body.demandAuditorId = this.confirmForm.demandAuditorId
          } else {
            body.needDemandAudit = false
          }
          const res = await demandConfirmInterestCalculation(this.recordId, body)
          const data = res.data
          if (data && data.success && data.data) {
            this.record = { ...this.record, ...data.data }
            this.confirmDialogVisible = false
            this.$message.success('确认成功')
          } else {
            this.$message.error((data && data.message) || '确认失败')
          }
        } catch (error) {
          const msg = (error.response && error.response.data && error.response.data.message) || error.message || '确认失败'
          this.$message.error(msg)
        } finally {
          this.confirmLoading = false
        }
      })
    },
    async supplierAudit() {
      this.auditLoading = true
      try {
        const res = await supplierAuditInterestCalculation(this.recordId)
        const data = res.data
        if (data && data.success && data.data) {
          this.record = { ...this.record, ...data.data }
          this.$message.success('供应商审核通过')
        } else {
          this.$message.error((data && data.message) || '审核失败')
        }
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message) || error.message || '审核失败'
        this.$message.error(msg)
      } finally {
        this.auditLoading = false
      }
    },
    async demandAudit() {
      this.auditLoading = true
      try {
        const res = await demandAuditInterestCalculation(this.recordId)
        const data = res.data
        if (data && data.success && data.data) {
          this.record = { ...this.record, ...data.data }
          this.$message.success('需求方审核通过')
        } else {
          this.$message.error((data && data.message) || '审核失败')
        }
      } catch (error) {
        const msg = (error.response && error.response.data && error.response.data.message) || error.message || '审核失败'
        this.$message.error(msg)
      } finally {
        this.auditLoading = false
      }
    },
    rollbackRecord() {
      this.$confirm('确定将已确认的延期费用计算回退为「待确认」吗？回退后可重新进行需求方确认。（仅创建人可操作）', '回退确认', {
        confirmButtonText: '确定回退',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        this.rollbackLoading = true
        try {
          const res = await rollbackInterestCalculation(this.recordId)
          const data = res.data
          if (data && data.success && data.data) {
            this.record = { ...this.record, ...data.data }
            this.$message.success('状态回退成功')
          } else {
            this.$message.error((data && data.message) || '回退失败')
          }
        } catch (error) {
          const msg = (error.response && error.response.data && error.response.data.message) || error.message || '回退失败'
          this.$message.error(msg)
        } finally {
          this.rollbackLoading = false
        }
      }).catch(() => {})
    },
    buildSignatureImageUrl(filePath) {
      if (!filePath) return null
      if (filePath.startsWith('http://') || filePath.startsWith('https://')) return filePath
      if (filePath.startsWith('data:')) return filePath
      const apiUrl = getApiUrl('/attachments/download')
      return `${apiUrl}?filePath=${encodeURIComponent(filePath)}`
    },
    async handlePrint() {
      let signatureUrls = {}
      let loadingInstance = null
      try {
        loadingInstance = this.$loading({
          lock: true,
          text: '正在获取打印数据...',
          spinner: 'el-icon-loading',
          background: 'rgba(0, 0, 0, 0.7)'
        })
        const res = await getInterestCalculationPrintData(this.recordId)
        if (res.data && res.data.success && res.data.data) {
          const pd = res.data.data
          signatureUrls = {
            demandUnit: pd.demandUnitSignatureUrl ? this.buildSignatureImageUrl(pd.demandUnitSignatureUrl) : null,
            supplierOperator: pd.supplierOperatorSignatureUrl ? this.buildSignatureImageUrl(pd.supplierOperatorSignatureUrl) : null,
            demandAuditor: pd.demandAuditorSignatureUrl ? this.buildSignatureImageUrl(pd.demandAuditorSignatureUrl) : null,
            supplierAuditor: pd.supplierAuditorSignatureUrl ? this.buildSignatureImageUrl(pd.supplierAuditorSignatureUrl) : null
          }
        }
      } catch (e) {
        console.warn('获取打印签名数据失败，使用无签名打印', e)
      } finally {
        if (loadingInstance && loadingInstance.close) loadingInstance.close()
      }
      const content = this.generatePrintContent(signatureUrls)
      const printWindow = window.open('', '_blank', 'width=800,height=600')
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>延期费用明细 - 打印</title>
          <style>
            body { font-family: "Microsoft YaHei", Arial, sans-serif; font-size: 14px; padding: 20px; }
            .print-title { text-align: center; font-size: 20px; font-weight: bold; margin-bottom: 20px; }
            .print-info { margin-bottom: 16px; }
            .print-row { margin: 8px 0; }
            .print-label { display: inline-block; width: 100px; }
            .print-value { background: #fffacd; padding: 2px 8px; }
            .print-table { width: 100%; border-collapse: collapse; margin: 16px 0; }
            .print-table th, .print-table td { border: 1px solid #333; padding: 8px; text-align: left; }
            .print-table th { background: #f5f5f5; text-align: center; }
            .print-table .print-summary-row td { font-weight: 500; }
            .print-signature-table { border: none; table-layout: fixed; width: 100%; margin-top: 40px; }
            .print-signature-table td { border: none; padding: 12px 8px; font-size: 13px; vertical-align: middle; width: 25%; }
            .print-signature-table .signature-label { font-weight: normal; }
            .print-signature-table .signature-value { margin-left: 4px; }
            .print-signature-table img { max-width: 150px; max-height: 80px; display: inline-block; vertical-align: middle; margin-left: 10px; object-fit: contain; }
            @media print { body { padding: 0; } }
          </style>
        </head>
        <body>${content}</body>
        </html>
      `)
      await installPlatformPrintFooter(printWindow, null, {
        documentType: 'INTEREST_CALCULATION',
        documentKey: this.recordId
      })
      printWindow.document.close()
      printWindow.onload = () => {
        printWindow.focus()
        printWindow.print()
        printWindow.onafterprint = () => printWindow.close()
      }
    },
    generatePrintContent(signatureUrls = {}) {
      const r = this.record
      const list = this.settlementList || []
      const fmtDate = (val) => {
        if (!val) return ''
        const d = new Date(val)
        return isNaN(d.getTime()) ? val : `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      }
      const buildSig = (url) => url ? `<img src="${url}" alt="签名" />` : ''
      const hasConfirmation = !!(r.confirmationTime || r.confirmation_time) || r.status === 'EXECUTING'
      const hasSupplierOp = !!(r.createTime || r.create_time)
      const hasDemandAudit = !!(r.demandAuditTime || r.demand_audit_time)
      const hasSupplierAudit = !!(r.supplierAuditTime || r.supplier_audit_time)
      let rows = list.map(row => `
        <tr>
          <td>${row.settlementNumber || '-'}</td>
          <td>${row.settlementTitle || '-'}</td>
          <td>${this.formatSettlementDate(row.settlementDate)}</td>
          <td>${this.formatAmount(row.settlementAmount)}</td>
          <td>${this.formatAmount(row.paidAmount)}</td>
          <td>${this.formatAmount(row.remainingAmount)}</td>
          <td>${this.formatAmount(row.interestAmount)}</td>
        </tr>
      `).join('')
      rows += `
        <tr class="print-summary-row"><td colspan="3">合计</td><td>${this.formatAmount(r.settlementTotalAmount)}</td><td>${this.formatAmount(this.settlementPaidTotal)}</td><td>${this.formatAmount(this.settlementRemainingTotal)}</td><td>${this.formatAmount(r.interestTotalAmount)}</td></tr>
        <tr class="print-summary-row"><td colspan="6">已结算延期费用</td><td>${this.formatAmount(r.settledInterest)}</td></tr>
        <tr class="print-summary-row"><td colspan="6">本期新增延期费用</td><td>${this.formatAmount(r.newInterest)}</td></tr>
      `
      const demandUnitSig = hasConfirmation ? buildSig(signatureUrls.demandUnit) : ''
      const supplierOpSig = hasSupplierOp ? buildSig(signatureUrls.supplierOperator) : ''
      const demandAuditorSig = hasDemandAudit ? buildSig(signatureUrls.demandAuditor) : ''
      const supplierAuditorSig = hasSupplierAudit ? buildSig(signatureUrls.supplierAuditor) : ''
      return `
      <div class="print-title">延期费用明细</div>
      <div class="print-info">
        <div class="print-row"><span class="print-label">截止时间</span><span class="print-value">${this.formatCutoffDate(r.cutoffDate)}</span></div>
        <div class="print-row"><span class="print-label">合同名称</span><span class="print-value">${r.contractName || '-'}</span></div>
        <div class="print-row"><span class="print-label">需求方名称</span><span class="print-value">${r.demandUnitNames || r.demandUnitName || ''} ${r.projectName || ''}</span></div>
      </div>
      <table class="print-table">
        <thead><tr><th>结算单号</th><th>结算单标题</th><th>结算时间</th><th>结算金额</th><th>已付金额</th><th>未付金额</th><th>延期费用金额</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <table class="print-signature-table">
        <tr>
          <td><span class="signature-label">需求单位经办人：</span>${demandUnitSig}</td>
          <td><span class="signature-label">操作时间：</span><span class="signature-value">${hasConfirmation ? fmtDate(r.confirmationTime || r.confirmation_time) : ''}</span></td>
          <td><span class="signature-label">供应单位经办人：</span>${supplierOpSig}</td>
          <td><span class="signature-label">操作时间：</span><span class="signature-value">${hasSupplierOp ? fmtDate(r.createTime || r.create_time) : ''}</span></td>
        </tr>
        <tr>
          <td><span class="signature-label">需求单位审核人：</span>${demandAuditorSig}</td>
          <td><span class="signature-label">操作时间：</span><span class="signature-value">${hasDemandAudit ? fmtDate(r.demandAuditTime || r.demand_audit_time) : ''}</span></td>
          <td><span class="signature-label">供应单位审核人：</span>${supplierAuditorSig}</td>
          <td><span class="signature-label">操作时间：</span><span class="signature-value">${hasSupplierAudit ? fmtDate(r.supplierAuditTime || r.supplier_audit_time) : ''}</span></td>
        </tr>
      </table>
      `
    },
    goBack() {
      this.$router.push('/finance/interest-calculation')
    },
    formatCutoffDate(str) {
      if (!str) return '-'
      return str.replace(/-/g, '/')
    },
    formatSettlementDate(str) {
      if (!str) return '-'
      const d = new Date(str)
      if (isNaN(d.getTime())) return str
      return d.toLocaleDateString('zh-CN').replace(/\//g, '-')
    },
    formatDateTime(str) {
      if (!str) return '-'
      const d = new Date(str)
      if (isNaN(d.getTime())) return str
      return d.toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).replace(/\//g, '-')
    },
    formatAmount(amount) {
      if (amount === undefined || amount === null) return '0.00'
      return Number(amount).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },
    // 利率类型：FIXED=固定，VARIABLE=变动
    formatInterestRateType(type) {
      if (!type) return '-'
      const map = { FIXED: '固定', VARIABLE: '变动' }
      return map[type] || type
    },
    // 实际计算利率（百分比显示）
    formatAppliedInterestRate(rate) {
      if (rate === undefined || rate === null) return '-'
      return `${Number(rate).toFixed(2)}%`
    },
    formatPrintDate(val) {
      if (!val) return ''
      const d = new Date(val)
      if (isNaN(d.getTime())) return val
      const y = d.getFullYear()
      const m = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return `${y}-${m}-${day}`
    },
    getSummaryMethod({ columns }) {
      const sums = []
      columns.forEach((col, index) => {
        if (index === 0) sums[index] = '合计'
        else if (index === 4) sums[index] = this.formatAmount(this.record.settlementTotalAmount)
        else if (index === 5) sums[index] = this.formatAmount(this.settlementPaidTotal)
        else if (index === 6) sums[index] = this.formatAmount(this.settlementRemainingTotal)
        else if (index === 8) sums[index] = this.formatAmount(this.record.interestTotalAmount)
        else sums[index] = ''
      })
      return sums
    }
  }
}
</script>

<style lang="scss" scoped>
.interest-calculation-detail {
  padding: 20px;
  background: #f5f7fa;
  min-height: calc(100vh - 84px);
}

.loading-wrap {
  padding: 20px;
  background: #fff;
  border-radius: 4px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background: #fff;
  border-radius: 4px;

  .header-title {
    display: flex;
    align-items: center;
    font-size: 18px;
    font-weight: bold;

    .title-mark {
      width: 4px;
      height: 18px;
      background: #409eff;
      margin-right: 10px;
    }
  }
}

.info-card,
.table-card {
  margin-bottom: 20px;
  background: #fff;

  .card-header {
    font-size: 16px;
    font-weight: bold;
  }
}

.amount-text { color: #303133; font-weight: 500; }
.interest-text { color: #e6a23c; font-weight: 600; }
.unpaid-interest { color: #f56c6c; font-weight: 600; }
.cell-multiline { white-space: pre-line; }

.payment-detail-dialog {
  .dialog-settlement-info { margin-bottom: 12px; font-size: 14px; padding: 8px; background: #f5f7fa; border-radius: 4px; }
}

/* 打印区域 - 屏幕默认隐藏 */
.print-only {
  position: absolute;
  left: -9999px;
  top: 0;
  width: 800px;
}

@media print {
  .hide-on-print {
    display: none !important;
  }
}
</style>
